import { useState } from "react";
import { toast } from "sonner";
import { Wand2, Undo2, Loader2 } from "lucide-react";
import { Slider } from "../ui/slider";
import { api, errMsg } from "../../lib/api";

const MODES = [
  ["normal", "Normal"],
  ["soft-light", "Soft light"],
  ["luminosity", "Luminosity"],
  ["multiply", "Multiply"],
  ["screen", "Screen"],
  ["duotone", "Club colours"],
];

export const BackgroundRemover = ({ photo, onChange, prefix = "photo", done = "Background removed – your player now stands on the shield background", restoreLabel = "Restore original photo" }) => {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/photos/remove-background", { file_id: photo.file.id, kind: prefix === "crest" ? "crest" : "photo" }, { timeout: 200000 });
      onChange({ ...photo, file: data, original: photo.original || photo.file });
      toast.success(done);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {!photo.original ? (
        <button type="button" onClick={remove} disabled={busy} data-testid={`${prefix}-remove-bg-button`}
          className="flex items-center gap-2 bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-slate-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} {busy ? "Removing background… (~15s)" : "Remove background"}
        </button>
      ) : (
        <button type="button" onClick={() => onChange({ ...photo, file: photo.original, original: null })} data-testid={`${prefix}-restore-original-button`}
          className="flex items-center gap-1.5 border-2 border-slate-200 px-3 py-2 text-xs font-bold uppercase hover:border-slate-900">
          <Undo2 className="h-3.5 w-3.5" /> {restoreLabel}
        </button>
      )}
    </div>
  );
};

export const BlendControls = ({ photo, onChange }) => {
  const mode = photo.blend || "normal";
  return (
    <div className="space-y-3">
      <div className="text-sm font-bold">Blend photo into background</div>
      <div className="grid grid-cols-3 gap-1.5" data-testid="photo-blend-modes">
        {MODES.map(([id, label]) => (
          <button key={id} type="button" onClick={() => onChange({ ...photo, blend: id, blendStrength: photo.blendStrength ?? 0.7 })} data-testid={`photo-blend-${id}`}
            className={`border-2 px-2 py-1.5 text-[11px] font-bold uppercase ${mode === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-900"}`}>{label}</button>
        ))}
      </div>
      {mode !== "normal" && (
        <div>
          <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-wider text-slate-600"><span>Blend strength</span><span>{Math.round((photo.blendStrength ?? 0.7) * 100)}%</span></div>
          <Slider min={0.1} max={1} step={0.01} value={[photo.blendStrength ?? 0.7]} onValueChange={([v]) => onChange({ ...photo, blendStrength: v })} data-testid="photo-blend-strength" />
        </div>
      )}
    </div>
  );
};
